import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import LearnShell, { useBreakpoint, useNavOffset } from "../shell/LearnShell";
import LearnImage from "../media/LearnImage";
import LearnCompanion from "../LearnCompanion";
import { LEARN, stickerButton, usePrefersReducedMotion } from "../theme";
import { LEARN_UI, learnAssetUrl } from "../media/learnAssets";
import { wideMapFor } from "../../../data/learnWideMaps";

/**
 * Shared world-map surface for Letters and Counting worlds.
 *
 * Desktop: the world's portrait art is the centrepiece at full height with a
 * winding SVG trail drawn through the nodes, flanked by an info rail (title,
 * progress, up-next card) and Pip. The same art, blurred, fills the page
 * behind everything so the world feels wide, not letterboxed.
 * Mobile: full-bleed art with overlaid chrome and a bottom dock.
 *
 * nodes: [{ id, label, sub, state: complete|current|locked, x, y (0..1 of art), onSelect }]
 */
export default function WorldMap({
  title,
  eyebrow,
  artKey,
  artAspect = 853 / 1844,
  accent = LEARN.color.blue,
  accentDeep = LEARN.color.blueDeep,
  ink = LEARN.color.ink,
  nodes = [],
  progress = { done: 0, total: 0 },
  next,               // { title, subtitle, onStart, disabled, cta }
  onBack,
  backLabel = "All worlds",
  companionPose = "idle",
  tip,
  nodeScale,
  children,          // extra overlays inside the art box (signposts, decorations)
  scrollable = false, // tall art (e.g. 1:3): page scrolls, rails stick
  scrollToNodeId,     // bring this node into view on mount
  wide: wideProp,     // { art, aspect, nodes } landscape art for desktop; defaults from learnWideMaps
}) {
  const bp = useBreakpoint();
  const compact = bp === "xs" || bp === "sm";
  const wide = !compact ? (wideProp === undefined ? wideMapFor(artKey) : wideProp) : null;
  const [hovered, setHovered] = useState(null);
  // Desktop uses the landscape painting when one exists; phones keep the portrait.
  const art = wide ? wide.art : artKey;
  const aspect = wide ? wide.aspect : artAspect;
  const placedRaw = wide ? nodes.map((n, i) => ({ ...n, x: wide.nodes[i]?.x ?? n.x, y: wide.nodes[i]?.y ?? n.y })) : nodes;
  const tall = scrollable && !wide;
  const reduceMotion = usePrefersReducedMotion();
  const { navOffset } = useNavOffset();
  const artBox = useRef(null);
  const stageRef = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [stage, setStage] = useState(() => (typeof window === "undefined" ? { w: 1440, h: 800 } : { w: window.innerWidth, h: window.innerHeight }));

  useLayoutEffect(() => {
    const el = artBox.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([e]) => setBox({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([e]) => setStage({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [wide]);

  // Full-screen desktop map: the painting covers the viewport (like CSS
  // object-fit: cover) and the nodes live inside the painting's box so their
  // fractional coordinates stay exact whatever the window shape.
  const coverScale = wide ? Math.max(stage.w / 1344, stage.h / 768) : 1;
  const coverW = 1344 * coverScale;
  const coverH = 768 * coverScale;
  // Cover-fit crops the painting's edges; keep every stop (plus its shadow)
  // inside the visible part so nothing is cut off on short or narrow windows.
  const placed = wide
    ? placedRaw.map((n) => {
        const cropX = (coverW - stage.w) / 2 / coverW;
        const cropY = (coverH - stage.h) / 2 / coverH;
        const padX = 0.07 * (1344 / coverW) + cropX;
        const padY = 0.11 * (768 / coverH) + cropY;
        return { ...n, x: Math.min(1 - padX, Math.max(padX, n.x)), y: Math.min(1 - padY, Math.max(padY, n.y)) };
      })
    : placedRaw;

  const nodeSize = wide
    ? Math.max(56, Math.min(96, stage.w * 0.058))
    : Math.max(52, Math.min(96, box.w * (nodeScale || 0.17)));

  useEffect(() => {
    if (!scrollToNodeId || !artBox.current) return undefined;
    const el = artBox.current.querySelector(`[data-node-id="${scrollToNodeId}"]`);
    if (!el) return undefined;
    const id = window.setTimeout(() => el.scrollIntoView({ block: "center", behavior: reduceMotion ? "auto" : "smooth" }), 250);
    return () => window.clearTimeout(id);
  }, [scrollToNodeId, reduceMotion]);
  const path = smoothTrail(placed);

  const Trail = (
    <div
      ref={artBox}
      style={wide ? {
        position: "absolute", width: coverW, height: coverH,
        left: (stage.w - coverW) / 2, top: (stage.h - coverH) / 2,
      } : {
        position: "relative", aspectRatio: `${aspect}`,
        height: compact || tall ? undefined : `calc(100vh - ${navOffset + 48}px)`,
        width: compact ? "100%" : tall ? "min(460px, 34vw)" : undefined,
        maxHeight: compact || tall ? undefined : 960, minHeight: compact ? "100vh" : tall ? undefined : 520,
        borderRadius: compact ? 0 : LEARN.radius.xl, overflow: "hidden",
        boxShadow: compact ? "none" : LEARN.shadow.float, border: compact ? "none" : "3px solid rgba(255,255,255,0.7)",
        margin: "0 auto",
      }}
    >
      <LearnImage asset={art} fit="cover" eager style={{ position: "absolute", inset: 0 }} />
      {typeof children === "function" ? children({ wide: !!wide, compact }) : children}
      {/* winding trail */}
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
        {path && (
          <>
            <path d={path} fill="none" stroke="rgba(255,255,255,0.55)" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" style={{ strokeWidth: wide ? 7 : 10 }} />
            <path d={path} fill="none" stroke={accent} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" strokeDasharray="0 9" style={{ strokeWidth: wide ? 3.5 : 4, opacity: 0.9 }} />
          </>
        )}
      </svg>
      {placed.map((n, i) => (
        <MapNode key={n.id} node={n} size={nodeSize} accent={accent} accentDeep={accentDeep} index={i} reduceMotion={reduceMotion} compact={compact}
          showCaption={!wide || n.state === "current" || hovered === n.id} showCaptionSide={!wide} onHover={setHovered} />
      ))}
    </div>
  );

  const InfoRail = (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
      <button type="button" onClick={onBack} style={{ all: "unset", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: LEARN.font, fontWeight: 700, fontSize: 14, color: LEARN.color.blue, background: "#fff", padding: "10px 16px 10px 12px", borderRadius: 999, boxShadow: LEARN.shadow.card, alignSelf: "flex-start" }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
        {backLabel}
      </button>
      <div style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(10px)", borderRadius: LEARN.radius.lg, padding: "22px 22px 20px", boxShadow: LEARN.shadow.card, border: `2px solid ${LEARN.color.line}` }}>
        {eyebrow && <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", color: accentDeep }}>{eyebrow}</div>}
        <h1 style={{ margin: "6px 0 10px", fontSize: 30, lineHeight: 1.05, fontWeight: 700, color: ink, letterSpacing: "-0.01em" }}>{title}</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1, height: 10, borderRadius: 999, background: "rgba(11,26,51,0.08)", overflow: "hidden" }}>
            <motion.div initial={{ width: 0 }} animate={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} style={{ height: "100%", background: accent, borderRadius: 999 }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: LEARN.color.muted, whiteSpace: "nowrap" }}>{progress.done} of {progress.total}</span>
        </div>
      </div>
      {next && (
        <div style={{ background: "#fff", borderRadius: LEARN.radius.lg, padding: "18px 20px", boxShadow: LEARN.shadow.card, border: `2px solid ${accent}55` }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", color: LEARN.color.muted }}>Up next</div>
          <div style={{ fontSize: 20, fontWeight: 700, margin: "4px 0 2px" }}>{next.title}</div>
          {next.subtitle && <div style={{ fontSize: 14, color: LEARN.color.muted }}>{next.subtitle}</div>}
          <button type="button" onClick={next.onStart} disabled={next.disabled} style={{ ...stickerButton({ bg: accent, deep: accentDeep }), width: "100%", marginTop: 14, opacity: next.disabled ? 0.5 : 1, cursor: next.disabled ? "not-allowed" : "pointer" }}>
            {next.cta || "Start"}
          </button>
        </div>
      )}
    </div>
  );

  if (compact) {
    return (
      <div style={{ position: "relative", fontFamily: LEARN.font, background: "#BEE3FF", minHeight: "100vh" }}>
        {Trail}
        <div style={{ position: "absolute", left: 0, right: 0, top: navOffset - 8, padding: "0 14px", display: "flex", alignItems: "center", gap: 10, pointerEvents: "none" }}>
          <button type="button" onClick={onBack} aria-label={backLabel} style={{ pointerEvents: "auto", width: 44, height: 44, borderRadius: "50%", border: "2px solid #fff", background: "rgba(255,255,255,0.95)", boxShadow: LEARN.shadow.card, cursor: "pointer", display: "grid", placeItems: "center" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={LEARN.color.ink} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
          </button>
          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#fff", textShadow: "0 2px 8px rgba(11,26,51,0.5)" }}>{title}</div>
            <div style={{ display: "inline-block", marginTop: 4, fontSize: 12, fontWeight: 700, background: "rgba(255,255,255,0.92)", padding: "4px 12px", borderRadius: 999 }}>{progress.done} of {progress.total}</div>
          </div>
          <div style={{ width: 44 }} />
        </div>
        {next && (
          <div style={{ position: "fixed", left: 12, right: 12, bottom: "max(12px, env(safe-area-inset-bottom))", background: "#fff", borderRadius: LEARN.radius.lg, padding: "12px 12px 12px 16px", boxShadow: LEARN.shadow.float, display: "flex", alignItems: "center", gap: 12, zIndex: 40 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{next.title}</div>
              {next.subtitle && <div style={{ fontSize: 12.5, color: LEARN.color.muted }}>{next.subtitle}</div>}
            </div>
            <button type="button" onClick={next.onStart} disabled={next.disabled} style={{ ...stickerButton({ bg: accent, deep: accentDeep, size: "sm" }), opacity: next.disabled ? 0.5 : 1 }}>{next.cta || "Start"}</button>
          </div>
        )}
      </div>
    );
  }

  if (wide) {
    return (
      <div ref={stageRef} style={{ position: "relative", height: "100vh", minHeight: 560, overflow: "hidden", fontFamily: LEARN.font, background: "#BEE3FF" }}>
        {Trail}
        {/* soft vignette so the floating cards and the bottom edge read cleanly */}
        <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(90deg, rgba(11,26,51,0.22) 0%, rgba(11,26,51,0) 34%), linear-gradient(180deg, rgba(11,26,51,0.10) 0%, rgba(11,26,51,0) 22%)" }} />
        <div style={{ position: "absolute", left: "clamp(16px, 2.2vw, 32px)", top: navOffset + 12, width: "clamp(250px, 21vw, 310px)", zIndex: 5 }}>
          {InfoRail}
        </div>
        <div style={{ position: "absolute", right: "clamp(12px, 2vw, 28px)", bottom: 10, display: "flex", alignItems: "flex-end", gap: 12, pointerEvents: "none", zIndex: 5 }}>
          {tip && <div style={{ marginBottom: 34, background: "rgba(255,255,255,0.94)", borderRadius: 16, padding: "10px 14px", fontSize: 14, fontWeight: 600, color: LEARN.color.ink, boxShadow: LEARN.shadow.card, maxWidth: 210 }}>{tip}</div>}
          <LearnCompanion size={Math.max(140, Math.min(200, stage.h * 0.22))} pose={companionPose} />
        </div>
      </div>
    );
  }

  return (
    <LearnShell
      maxWidth={1400}
      padBottom={24}
      background={`linear-gradient(180deg, ${accent}22 0%, #EAF3FF 100%)`}
      style={{ position: "relative", overflow: "hidden" }}
    >
      {/* ambient: the world art, blurred, filling the page behind the map */}
      <div aria-hidden="true" style={{ position: tall ? "fixed" : "absolute", inset: 0, backgroundImage: `url(${learnAssetUrl(art)})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(28px) saturate(1.1)", opacity: 0.45, transform: "scale(1.15)", zIndex: 0 }} />
      <div style={{ position: "relative", zIndex: 1, display: "grid", gridTemplateColumns: "minmax(240px, 320px) minmax(0, 1fr) minmax(180px, 240px)", gap: 24, alignItems: tall ? "start" : "center" }}>
        <div style={tall ? { position: "sticky", top: navOffset + 8 } : undefined}>{InfoRail}</div>
        {Trail}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, ...(tall ? { position: "sticky", top: navOffset + 8 } : {}) }}>
          <LearnCompanion size={180} pose={companionPose} />
          {tip && <div style={{ background: "rgba(255,255,255,0.9)", borderRadius: LEARN.radius.md, padding: "12px 16px", fontSize: 14, color: LEARN.color.muted, textAlign: "center", boxShadow: LEARN.shadow.card }}>{tip}</div>}
        </div>
      </div>
    </LearnShell>
  );
}

/** Catmull-Rom → cubic Bézier through the node centres (percent coords). */
function smoothTrail(pts) {
  if (pts.length < 2) return "";
  const P = pts.map((n) => ({ x: n.x * 100, y: n.y * 100 }));
  let d = `M ${P[0].x.toFixed(2)} ${P[0].y.toFixed(2)}`;
  for (let i = 0; i < P.length - 1; i += 1) {
    const p0 = P[i - 1] || P[i];
    const p1 = P[i];
    const p2 = P[i + 1];
    const p3 = P[i + 2] || p2;
    const c1 = { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 };
    const c2 = { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 };
    d += ` C ${c1.x.toFixed(2)} ${c1.y.toFixed(2)}, ${c2.x.toFixed(2)} ${c2.y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return d;
}

function MapNode({ node, size, accent, accentDeep, index, reduceMotion, compact, showCaption = true, showCaptionSide = true, onHover }) {
  const complete = node.state === "complete";
  const current = node.state === "current";
  const locked = node.state === "locked";
  const bg = complete ? LEARN.color.mint : current ? accent : "rgba(255,255,255,0.72)";
  const deep = complete ? LEARN.color.mintDeep : current ? accentDeep : "rgba(11,26,51,0.18)";
  const captionSide = node.captionSide || (node.x > 0.5 ? "left" : "right");
  const captionAbove = !showCaptionSide; // wide maps: caption floats above the node
  return (
    <>
    {node.caption && showCaption && (
      <motion.div
        aria-hidden="true"
        initial={reduceMotion ? false : { opacity: 0, x: captionSide === "left" ? 8 : -8 }}
        animate={{ opacity: locked ? 0.7 : 1, x: 0 }}
        transition={{ delay: 0.25 + index * 0.07, duration: 0.4 }}
        style={{
          position: "absolute",
          ...(captionAbove
            ? { left: `${node.x * 100}%`, bottom: `calc(${(1 - node.y) * 100}% + ${size * 0.62}px)`, transform: "translateX(-50%)", textAlign: "center", whiteSpace: "nowrap" }
            : { top: `${node.y * 100}%`, transform: "translateY(-50%)", textAlign: captionSide === "left" ? "right" : "left",
                ...(captionSide === "left" ? { right: `calc(${(1 - node.x) * 100}% + ${size * 0.62}px)` } : { left: `calc(${node.x * 100}% + ${size * 0.62}px)` }) }),
          maxWidth: captionAbove ? 220 : compact ? 132 : 170, background: current ? "#fff" : "rgba(255,255,255,0.9)", borderRadius: 14,
          padding: compact ? "6px 10px" : "8px 12px", boxShadow: LEARN.shadow.card, border: `2px solid ${current ? accent : "rgba(255,255,255,0.9)"}`,
          fontFamily: LEARN.font, pointerEvents: "none", zIndex: 4,
        }}
      >
        <div style={{ fontSize: compact ? 12.5 : 14, fontWeight: 700, color: LEARN.color.ink, lineHeight: 1.15 }}>{node.caption}</div>
        {node.sub && !compact && <div style={{ fontSize: 11.5, color: LEARN.color.muted, marginTop: 2, lineHeight: 1.2 }}>{node.sub}</div>}
      </motion.div>
    )}
    <motion.button
      type="button"
      onClick={locked ? undefined : node.onSelect}
      disabled={locked}
      data-node-id={node.id}
      onPointerEnter={() => onHover?.(node.id)}
      onPointerLeave={() => onHover?.(null)}
      onFocus={() => onHover?.(node.id)}
      onBlur={() => onHover?.(null)}
      aria-label={`${node.caption || node.label}${node.sub ? ` — ${node.sub}` : ""}: ${node.state}`}
      initial={reduceMotion ? false : { scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 0.15 + index * 0.07, type: "spring", stiffness: 380, damping: 22 }}
      whileHover={locked || reduceMotion ? undefined : { scale: 1.08 }}
      whileTap={locked ? undefined : { scale: 0.94 }}
      style={{
        position: "absolute", left: `${node.x * 100}%`, top: `${node.y * 100}%`, transform: "translate(-50%, -50%)", zIndex: 3,
        width: size, height: size, borderRadius: "50%", border: `3px solid ${complete || current ? "#fff" : "rgba(255,255,255,0.8)"}`,
        background: bg, color: locked ? LEARN.color.muted : "#fff", boxShadow: `0 ${current ? 6 : 4}px 0 ${deep}, 0 10px 20px rgba(11,26,51,0.2)`,
        fontFamily: LEARN.font, fontWeight: 800, fontSize: size * 0.42, cursor: locked ? "not-allowed" : "pointer",
        display: "grid", placeItems: "center", opacity: locked ? 0.85 : 1,
      }}
    >
      {current && !reduceMotion && (
        <motion.span aria-hidden="true" animate={{ scale: [1, 1.5], opacity: [0.6, 0] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
          style={{ position: "absolute", inset: -3, borderRadius: "50%", border: `3px solid ${accent}` }} />
      )}
      {complete ? (
        <img src={LEARN_UI("map_check_medal")} alt="" aria-hidden="true" onError={(e) => { e.currentTarget.replaceWith(document.createTextNode("✓")); }} style={{ width: "62%", height: "62%", objectFit: "contain" }} />
      ) : locked ? (
        <img src={LEARN_UI("map_padlock")} alt="" aria-hidden="true" onError={(e) => { e.currentTarget.style.display = "none"; }} style={{ width: "52%", height: "52%", objectFit: "contain", opacity: 0.9 }} />
      ) : (
        node.label
      )}
    </motion.button>
    </>
  );
}
